import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  Col,
  Container,
  Row,
} from 'react-bootstrap';
import type { IconType } from 'react-icons';
import {
  LuArrowRight,
  LuAward,
  LuCalculator,
  LuCalendar,
  LuChartLine,
  LuCircleCheck,
  LuClock,
  LuFileText,
  LuHandshake,
  LuLaptop,
  LuMegaphone,
  LuSearch,
  LuSettings,
  LuShieldCheck,
  LuTarget,
  LuTrendingUp,
  LuUserCheck,
  LuUsers,
} from 'react-icons/lu';

import AdvantageTilesGrid from '../app/components/AdvantageTilesGrid';
import HeroCtaActions from '../app/components/HeroCtaActions';
import RoleLinkCard from '../app/components/RoleLinkCard';
import GradientCTA from '../app/components/GradientCTA';
import HeroAnimated from '../app/components/HeroAnimated';
import ImpactResultsSection from '../app/components/ImpactResultsSection';
import RevealSection from '../app/components/RevealSection';
import RetingentProcessFlow from '../app/components/RetingentProcessFlow';
import SbrIcon from '../app/components/SbrIcon';
import { SBR_IMPACT_STATS } from '../data/sbrImpactStats';

const RETINGENT_HEADER_IMG = `${process.env.PUBLIC_URL}/images/retingentheader.png`;

const heroFeatures = [
  {
    title: 'Dedicated search like retained',
    body: 'A recruiter focused on your role from day one.',
    icon: 'bi-bullseye',
  },
  {
    title: 'Speed and flexibility like contingent',
    body: 'Move fast without long upfront commitments.',
    icon: 'bi-lightning-charge',
  },
  {
    title: 'Lower risk. Better outcomes.',
    body: 'Balanced fees and a proactive search model.',
    icon: 'bi-shield-check',
  },
];

type CompareCell = { type: 'check' } | { type: 'text'; value: string; bold?: boolean };

type CompareRow = {
  feature: string;
  contingent: CompareCell;
  retingent: CompareCell;
  retained: CompareCell;
};

const compareRows: CompareRow[] = [
  {
    feature: 'Proactive Search',
    contingent: { type: 'text', value: 'Limited' },
    retingent: { type: 'check' },
    retained: { type: 'check' },
  },
  {
    feature: 'Dedicated Recruiter',
    contingent: { type: 'text', value: 'No' },
    retingent: { type: 'check' },
    retained: { type: 'check' },
  },
  {
    feature: 'Speed to Present',
    contingent: { type: 'text', value: 'Variable' },
    retingent: { type: 'text', value: 'Fast', bold: true },
    retained: { type: 'text', value: 'Standard' },
  },
  {
    feature: 'Quality of Candidates',
    contingent: { type: 'text', value: 'Variable' },
    retingent: { type: 'text', value: 'High', bold: true },
    retained: { type: 'text', value: 'High' },
  },
  {
    feature: 'Fee Structure',
    contingent: { type: 'text', value: 'Success Only' },
    retingent: { type: 'text', value: 'Success Based', bold: true },
    retained: { type: 'text', value: 'Upfront Retainer' },
  },
  {
    feature: 'Risk',
    contingent: { type: 'text', value: 'Higher' },
    retingent: { type: 'text', value: 'Lower', bold: true },
    retained: { type: 'text', value: 'Lowest' },
  },
];

const whyRetingentItems: { title: string; body: string; Icon: IconType }[] = [
  {
    title: 'Proactive, Not Posting & Praying',
    body: 'We actively source and engage the right talent—not just post and wait.',
    Icon: LuTarget,
  },
  {
    title: 'Higher Fill Rate',
    body: 'A dedicated search with strong alignment drives better candidate quality and close rates.',
    Icon: LuAward,
  },
  {
    title: 'Faster Time-to-Hire',
    body: 'We move with urgency and flexibility to meet your timelines and priorities.',
    Icon: LuClock,
  },
  {
    title: 'Lower Risk',
    body: 'Pay a success-based fee, with a guarantee for peace of mind.',
    Icon: LuShieldCheck,
  },
  {
    title: 'Better Outcomes',
    body: 'Stronger hires. Longer retention. Greater impact for your business.',
    Icon: LuChartLine,
  },
];

const processSteps: {
  title: string;
  body: string;
  Icon: IconType;
}[] = [
  {
    title: 'Understand',
    body: 'We align on your needs, priorities, and must-haves.',
    Icon: LuTarget,
  },
  {
    title: 'Proactive Search',
    body: 'We tap our network and market intelligence to build a qualified pipeline.',
    Icon: LuSearch,
  },
  {
    title: 'Engage & Assess',
    body: 'We engage top talent and conduct a rigorous evaluation.',
    Icon: LuUserCheck,
  },
  {
    title: 'Present & Advise',
    body: 'You review a curated slate of candidates. We advise and guide.',
    Icon: LuFileText,
  },
  {
    title: 'Hire & Support',
    body: 'You hire with confidence. We ensure a smooth transition and stay close.',
    Icon: LuHandshake,
  },
];

const roleCategories: { title: string; Icon: IconType }[] = [
  { title: 'Finance & Accounting', Icon: LuCalculator },
  { title: 'IT & Technology', Icon: LuLaptop },
  { title: 'Sales & Revenue', Icon: LuTrendingUp },
  { title: 'Operations & Supply Chain', Icon: LuSettings },
  { title: 'HR & People Operations', Icon: LuUsers },
  { title: 'Marketing & Communications', Icon: LuMegaphone },
];

function CompareCellContent({ cell }: { cell: CompareCell }) {
  if (cell.type === 'check') {
    return (
      <span className="rg-compare__check" aria-label="Yes">
        <SbrIcon icon={LuCircleCheck} size={12} />
      </span>
    );
  }
  return <span className={cell.bold ? 'rg-compare__text--bold' : undefined}>{cell.value}</span>;
}

function RetingentCompareTable() {
  return (
    <div className="table-responsive rg-compare__wrap">
      <table className="rg-compare__table table mb-0">
        <thead>
          <tr>
            <th scope="col" className="rg-compare__feature-col">
              Feature
            </th>
            <th scope="col">
              <span className="d-none d-lg-inline">Contingent</span>
              <span className="d-lg-none">Cont.</span>
            </th>
            <th scope="col" className="rg-compare__col--retingent">
              <span className="d-none d-lg-inline">Retingent</span>
              <span className="d-lg-none">Reting.</span>
            </th>
            <th scope="col">
              <span className="d-none d-lg-inline">Retained</span>
              <span className="d-lg-none">Retain.</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {compareRows.map((row) => (
            <tr key={row.feature}>
              <th scope="row">{row.feature}</th>
              <td>
                <CompareCellContent cell={row.contingent} />
              </td>
              <td className="rg-compare__col--retingent">
                <CompareCellContent cell={row.retingent} />
              </td>
              <td>
                <CompareCellContent cell={row.retained} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function RetingentRecruitmentPage() {
  return (
    <>
      <section className="rg-hero bg-white">
        <div className="rg-hero__main">
          <div className="rg-hero__media">
            <div className="solution-hero-media solution-hero-media--retingent">
              <img
                src={RETINGENT_HEADER_IMG}
                alt="Professionals reviewing candidates on a laptop"
                className="solution-hero-media__img"
                width={720}
                height={496}
                decoding="async"
              />
              <div className="solution-hero-media__stat solution-hero-media__stat--tagline">
                <div className="solution-hero-media__stat-icon" aria-hidden>
                  <SbrIcon icon={LuUsers} size={32} />
                </div>
                <div className="solution-hero-media__stat-copy">
                  <div className="rg-hero-tagline__line">Right Talent.</div>
                  <div className="rg-hero-tagline__line">Right Time.</div>
                  <div className="rg-hero-tagline__line">Right Impact.</div>
                </div>
              </div>
            </div>
            <div className="rg-hero__mobile-cta d-lg-none">
              <div className="rg-hero__media-cta">
                <HeroCtaActions block="rg-hero" outlineVariant="outline-primary" />
              </div>
            </div>
          </div>

          <Container className="rg-hero__container position-relative">
            <Breadcrumb className="solution-hero-breadcrumb rg-hero__breadcrumb mb-3">
              <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>
                Solutions
              </Breadcrumb.Item>
              <Breadcrumb.Item active>Retingent Recruitment</Breadcrumb.Item>
            </Breadcrumb>

            <Row className="rg-hero__row gy-4">
              <Col lg={7} className="rg-hero__copy-col rg-hero__copy">
                <HeroAnimated className="rg-hero__animated">
                  <div className="rg-hero__headline">
                    <div className="rg-hero__prose">
                      <span className="rg-hero__width-anchor" aria-hidden="true">
                        The speed of contingent.
                      </span>
                      <h1 data-hero-item className="display-5 fw-bold mb-2">
                        The best of <span className="text-gradient-teal-blue">retained.</span>
                        <span className="d-block mt-1 rg-hero__headline-subline">
                          The speed of <span className="text-primary">contingent.</span>
                        </span>
                      </h1>
                    </div>
                  </div>

                  <div className="rg-hero__body">
                    <p data-hero-item className="lead text-sbr-slate mb-2 rg-hero__lead">
                      Retingent is a recruiting strategy that blends the fill rate and dedication of Retained Search with
                      the speed and urgency of Contingent Recruiting.
                    </p>
                    <p data-hero-item className="lead text-sbr-slate mb-3 rg-hero__lead">
                      You get a committed partner, a proactive search, and exceptional talent, delivered faster, with less
                      risk.
                    </p>
                  </div>

                  <div data-hero-item className="rg-hero__actions d-none d-lg-flex flex-column flex-sm-row gap-3 mb-3">
                    <HeroCtaActions block="rg-hero" outlineVariant="outline-primary" />
                  </div>

                  <Row data-hero-item className="rg-hero__features g-3 mt-auto">
                    {heroFeatures.map((f, index) => (
                      <Col
                        xs={6}
                        lg={4}
                        className={
                          index === 2
                            ? 'rg-hero__feature-col rg-hero__feature-col--solo'
                            : 'rg-hero__feature-col'
                        }
                        key={f.title}
                      >
                        <div className="solution-hero-feature">
                          <div className="solution-hero-feature__icon">
                            <i className={`bi ${f.icon}`} aria-hidden />
                          </div>
                          <div className="solution-hero-feature__copy">
                            <div className="solution-hero-feature__title">{f.title}</div>
                            <p className="solution-hero-feature__body">{f.body}</p>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </HeroAnimated>
              </Col>
            </Row>
          </Container>
        </div>
      </section>

      <section className="rg-why bg-white py-5 py-lg-6">
        <Container>
          <RevealSection>
            <div className="sbr-section-header text-center mb-5">
              <h2 className="sbr-section__title fw-bold mb-3">Why Retingent?</h2>
              <p className="sbr-section__lead home-section-lead mb-0 mx-auto">
                Proactive search, stronger outcomes, and less risk than posting and waiting.
              </p>
            </div>
          </RevealSection>
          <AdvantageTilesGrid items={whyRetingentItems} colMd={6} colLg={4} colXl={4} />
        </Container>
      </section>

      <section className="rg-process hero-pattern text-white py-5 py-lg-6">
        <Container>
          <div className="rg-process__header text-center mb-5">
            <h2 className="sbr-section__title fw-bold mb-0">Our Retingent Recruitment Process</h2>
          </div>
          <RetingentProcessFlow steps={processSteps} />
        </Container>
      </section>

      <section className="rg-roles-compare bg-sbr-light py-5 py-lg-6 border-top border-secondary border-opacity-10">
        <RevealSection>
          <Container>
            <Row className="rg-roles-compare__row g-4 g-lg-0 align-items-stretch">
              <Col lg={5} className="rg-roles-compare__roles-col">
                <div className="rg-roles-compare__panel h-100">
                  <div className="rg-roles-compare__header text-center mb-4">
                    <h2 className="sbr-section__title sbr-section__title--accent-sm fw-bold mb-0">Common Roles We Fill</h2>
                  </div>
                  <div className="rg-roles-compare__roles-grid">
                    {roleCategories.map((role) => (
                      <RoleLinkCard
                        key={role.title}
                        item={{ title: role.title, Icon: role.Icon }}
                        to="/roles-we-fill"
                      />
                    ))}
                  </div>
                  <div className="rg-roles-compare__roles-link text-center mt-4">
                    <Link to="/roles-we-fill" className="rg-roles-compare__link fw-semibold text-decoration-none">
                      View all roles we fill <SbrIcon icon={LuArrowRight} className="ms-1" size={18} />
                    </Link>
                  </div>
                </div>
              </Col>

              <Col lg="auto" className="d-none d-lg-flex px-0">
                <div className="rg-roles-compare__divider" aria-hidden />
              </Col>

              <Col lg className="rg-roles-compare__compare-col">
                <div className="rg-roles-compare__panel h-100">
                  <div className="rg-compare__header text-center mb-4">
                    <h2 className="sbr-section__title sbr-section__title--accent-sm fw-bold mb-0">
                      How <span className="rg-compare__title-accent">Retingent</span> Compares
                    </h2>
                  </div>
                  <div className="rg-roles-compare__table-panel">
                    <RetingentCompareTable />
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </RevealSection>
      </section>

      <ImpactResultsSection
        reveal
        title="Results That Matter"
        headerAlign="center"
        stats={SBR_IMPACT_STATS}
      />

      <GradientCTA
        icon={<SbrIcon icon={LuCalendar} size={26} />}
        title={
          <>
            <div>Let&apos;s build your bench—faster.</div>
            <div className="sbr-cta__subtitle fw-normal opacity-90 mt-1 mb-0">
              Book a call to see how Retingent can work for your team.
            </div>
          </>
        }
      />
    </>
  );
}
